import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { api, errorMessage, Field, SecondaryButton, inputClass, ringStyle, clean, filtered } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";

const SOCIETIES = ["ASCAP", "BMI", "SESAC", "PRS for Music", "SOCAN", "GEMA", "SACEM", "APRA AMCOS", "IPRS", "COSON", "SAMRO", "Other"];

function RightsManager({ disabled }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { society: "", ipi_number: "", member_number: "", iswc: "", territory: "" } });

  const load = useCallback(async () => {
    try {
      const response = await api.get("rights");
      setRows(response.data?.result || []);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load your registrations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (data) => {
    try {
      const response = await api.post("rights/add", {
        society: data.society,
        ipi_number: data.ipi_number.trim(),
        member_number: data.member_number.trim() || undefined,
        iswc: data.iswc.trim() || undefined,
        territory: data.territory.trim() || undefined,
      });
      if (response.data?.success) {
        setRows((r) => [response.data.result, ...r]);
        reset();
        toast.success("Registration saved.");
      }
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the registration."));
    }
  };

  const remove = async (id) => {
    if (removingId) return;
    setRemovingId(id);
    try {
      await api.post("rights/remove", { id });
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove the registration."));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-8">
      <p className="text-gray-500">
        Add your IPI number and the performing-rights or collecting society you belong to, so royalties can be
        matched to you.
      </p>

      {!disabled && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Society" required error={errors.society?.message}>
              <select className={inputClass(errors.society)} style={ringStyle} {...register("society", { required: "Society is required." })}>
                <option value="">Select society</option>
                {SOCIETIES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </Field>
            <Field label="IPI number" required error={errors.ipi_number?.message}>
              <input
                type="text"
                inputMode="numeric"
                className={inputClass(errors.ipi_number)}
                style={ringStyle}
                placeholder="9-11 digits"
                {...filtered(register("ipi_number", {
                  required: "IPI number is required.",
                  pattern: { value: /^[0-9]{9,11}$/, message: "IPI number must be 9-11 digits." },
                }), clean.digits, 11)}
              />
            </Field>
            <Field label="Member number" error={errors.member_number?.message}>
              <input type="text" className={inputClass(errors.member_number)} style={ringStyle} {...filtered(register("member_number", { maxLength: { value: 40, message: "Too long." } }), clean.alnum, 40)} />
            </Field>
            <Field label="ISWC (optional)" error={errors.iswc?.message}>
              <input
                type="text"
                className={inputClass(errors.iswc)}
                style={ringStyle}
                placeholder="T-123.456.789-0"
                {...filtered(register("iswc", {
                  validate: (v) => !v.trim() || /^T-?[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]$/.test(v.trim()) || "ISWC must look like T-123.456.789-0.",
                }), clean.iswc, 15)}
              />
            </Field>
            <Field label="Territory (optional)" error={errors.territory?.message}>
              <input type="text" className={inputClass(errors.territory)} style={ringStyle} placeholder="e.g. US, Worldwide" {...register("territory", { maxLength: { value: 80, message: "Too long." } })} />
            </Field>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-full font-semibold text-white px-8 py-3 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: isSubmitting ? undefined : "#095228" }}
            >
              {isSubmitting ? "Saving..." : "Add registration"}
            </button>
          </div>
        </form>
      )}

      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Your registrations</h2>
        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-gray-500 text-sm">No registrations yet.</p>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">{r.society}</p>
                  <p className="text-gray-500">
                    IPI {r.ipi_number}
                    {r.member_number ? ` · Member ${r.member_number}` : ""}
                    {r.iswc ? ` · ISWC ${r.iswc}` : ""}
                    {r.territory ? ` · ${r.territory}` : ""}
                  </p>
                </div>
                <SecondaryButton className="!px-4 !py-1.5 text-sm" disabled={removingId === r.id} onClick={() => remove(r.id)}>
                  {removingId === r.id ? "Removing..." : "Remove"}
                </SecondaryButton>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function RightsHolders() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} title="Rights-holder registrations" allowDeactivated>
      <RightsManager disabled={account.isDeactivated} />
    </StudioGate>
  );
}
